import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "@/lib/api";
import { useT } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Search, ExternalLink, ShoppingBag } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2 }).format(n || 0);

export default function Catalog() {
  const { t } = useT();
  const [buckets, setBuckets] = useState([]);
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [products, setProducts] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 24;

  useEffect(() => {
    api.get("/catalog/buckets").then(({ data }) => setBuckets(data.buckets)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setProducts(null);
    try {
      const params = { page, limit };
      if (active !== "all") params.bucket = active;
      if (q) params.search = q;
      const { data } = await api.get("/catalog/products", { params });
      setProducts(data.products);
      setTotal(data.total);
    } catch (e) { toast.error(apiErr(e)); setProducts([]); }
  }, [active, q, page]);

  useEffect(() => { load(); }, [load]);

  const doSearch = (e) => { e.preventDefault(); setPage(1); setQ(search); };
  const totalCount = buckets.reduce((a, b) => a + b.count, 0);
  const pages = Math.ceil(total / limit);

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-white">{t("Catalog KLUS")}</h1>
          <p className="text-slate-400 text-sm mt-1">{totalCount} {t("produse din klusled.ro")}</p>
        </div>
        <a href="https://klusled.ro/magazin/" target="_blank" rel="noreferrer"
          className="flex items-center gap-1.5 text-sm text-accent hover:underline" data-testid="catalog-shop-link">
          <ShoppingBag className="w-4 h-4" /> {t("Deschide magazinul")}
        </a>
      </div>

      <form onSubmit={doSearch} className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="catalog-search"
            placeholder={t("Caută produs sau cod SKU…")} className="pl-10 bg-slate-900 border-slate-700 h-11" />
        </div>
        <Button type="submit" className="bg-primary hover:bg-primary/90 text-white h-11">{t("Caută")}</Button>
      </form>

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => { setActive("all"); setPage(1); }} data-testid="bucket-all"
          className={`px-4 py-2 rounded-full text-sm border transition-all ${active === "all" ? "border-primary bg-primary/15 text-white" : "border-slate-700 text-slate-400"}`}>
          {t("Toate")} <Badge className="ml-1 bg-slate-800 border-0 text-slate-400">{totalCount}</Badge>
        </button>
        {buckets.map((b) => (
          <button key={b.key} onClick={() => { setActive(b.key); setPage(1); }} data-testid={`bucket-${b.key}`}
            className={`px-4 py-2 rounded-full text-sm border transition-all ${active === b.key ? "border-primary bg-primary/15 text-white" : "border-slate-700 text-slate-400 hover:border-slate-600"}`}>
            {b.label} <Badge className="ml-1 bg-slate-800 border-0 text-slate-400">{b.count}</Badge>
          </button>
        ))}
      </div>

      {products === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : products.length === 0 ? (
        <p className="text-center text-slate-400 py-20">{t("Niciun produs găsit.")}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.map((p) => (
              <a key={p.wc_id} href={p.permalink} target="_blank" rel="noreferrer" data-testid={`product-${p.wc_id}`}
                className="rounded-2xl overflow-hidden border border-slate-800 bg-card hover:border-primary/40 transition-colors group">
                <div className="aspect-square bg-white/5 overflow-hidden">
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : <div className="w-full h-full bg-slate-800" />}
                </div>
                <div className="p-3">
                  <p className="text-sm text-white line-clamp-2 group-hover:text-accent transition-colors min-h-[2.5rem]">{p.name}</p>
                  <p className="text-[11px] text-slate-500 font-mono mt-1">{p.sku}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-mono text-sm text-accent">{p.price ? `${fmt(p.price)} lei` : "—"}</span>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-600 group-hover:text-accent" />
                  </div>
                </div>
              </a>
            ))}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="catalog-prev"
                className="border-slate-700 text-slate-200">{t("Anterior")}</Button>
              <span className="text-sm text-slate-400 font-mono px-2">{page} / {pages}</span>
              <Button variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="catalog-next"
                className="border-slate-700 text-slate-200">{t("Următor")}</Button>
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}
